<template>
  <div
    :class="[
      'flex flex-col justify-between border-e border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 transition-all duration-300 ease-in-out w-64',
      isMobile
        ? [
            'fixed',
            'left-0',
            'top-0',
            'z-40',
            'h-screen',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          ]
        : [
            'fixed',
            'left-0',
            'top-12',
            'z-30',
            'h-[calc(100vh-3rem)]',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          ]
    ]"
  >
    <div class="px-4 py-6 overflow-y-auto flex-1">
      <!-- Navigation -->
      <ul class="mt-6 space-y-1">
        <!-- Helper users see no navigation items -->
        <template v-if="authStore.isAuthenticated && !authStore.isHelper">
          <li>
            <router-link
              to="/"
              :class="[
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                $route.path === '/'
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
              ]"
              @click="handleLinkClick"
            >
              <i class="pi pi-fw pi-home layout-menuitem-icon menu-icon"></i>
              <span class="layout-menuitem-text">Dashboard</span>
            </router-link>
          </li>

          <li>
            <details
              class="group [&_summary::-webkit-details-marker]:hidden"
              :open="isEventsSectionActive"
            >
              <summary
                class="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <span class="flex items-center gap-2 text-sm font-medium">
                  <i class="pi pi-fw pi-calendar layout-menuitem-icon menu-icon"></i>
                  <span class="layout-menuitem-text">Events</span>
                </span>

                <span class="shrink-0 transition duration-300 group-open:rotate-180">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="size-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </span>
              </summary>

              <ul class="mt-2 space-y-1 px-4">
                <li>
                  <router-link
                    :to="{ path: '/events' }"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      isAllEventsActive
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-list layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">All Events</span>
                  </router-link>
                </li>
                <li v-for="option in EVENT_TYPE_OPTIONS" :key="option.value">
                  <router-link
                    :to="{ path: '/events', query: { type: option.value } }"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      isEventTypeFilterActive(option.value)
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i
                      :class="[
                        'pi pi-fw layout-menuitem-icon menu-icon',
                        getEventTypeIcon(option.value)
                      ]"
                    ></i>
                    <span class="layout-menuitem-text">{{ option.label }}</span>
                  </router-link>
                </li>
              </ul>
            </details>
          </li>

          <LiveNavSection :live-events="liveEvents" @link-click="handleLinkClick" />

          <li>
            <details class="group [&_summary::-webkit-details-marker]:hidden">
              <summary
                class="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <span class="flex items-center gap-2 text-sm font-medium">
                  <i class="pi pi-fw pi-book layout-menuitem-icon menu-icon"></i>
                  <span class="layout-menuitem-text">Songs</span>
                </span>

                <span class="shrink-0 transition duration-300 group-open:rotate-180">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="size-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </span>
              </summary>

              <ul class="mt-2 space-y-1 px-4">
                <li>
                  <router-link
                    to="/songs"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/songs'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-list layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">All Songs</span>
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/categories"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/categories'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-folder layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Categories</span>
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/genres"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/genres'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-tags layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Genres</span>
                  </router-link>
                </li>
              </ul>
            </details>
          </li>

          <li v-if="authStore.isAuthenticated && authStore.isAdmin">
            <router-link
              to="/users"
              :class="[
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                $route.path === '/users'
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
              ]"
              @click="handleLinkClick"
            >
              <i class="pi pi-fw pi-users layout-menuitem-icon menu-icon"></i>
              <span class="layout-menuitem-text">Users</span>
            </router-link>
          </li>

          <li v-if="authStore.isAuthenticated && authStore.isAdmin">
            <router-link
              to="/stats"
              :class="[
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                $route.path === '/stats'
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
              ]"
              @click="handleLinkClick"
            >
              <i class="pi pi-fw pi-chart-bar layout-menuitem-icon menu-icon"></i>
              <span class="layout-menuitem-text">Statistics</span>
            </router-link>
          </li>

          <li v-if="authStore.isAuthenticated && authStore.isAdmin">
            <router-link
              to="/settings"
              :class="[
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                $route.path === '/settings'
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
              ]"
              @click="handleLinkClick"
            >
              <i class="pi pi-fw pi-cog layout-menuitem-icon menu-icon"></i>
              <span class="layout-menuitem-text">Settings</span>
            </router-link>
          </li>

          <li>
            <details class="group [&_summary::-webkit-details-marker]:hidden">
              <summary
                class="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <span class="flex items-center gap-2 text-sm font-medium">
                  <i class="pi pi-fw pi-book layout-menuitem-icon menu-icon"></i>
                  <span class="layout-menuitem-text">Documentation</span>
                </span>

                <span class="shrink-0 transition duration-300 group-open:rotate-180">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="size-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </span>
              </summary>

              <ul class="mt-2 space-y-1 px-4">
                <li>
                  <router-link
                    to="/documentation/dashboard"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/documentation/dashboard'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-home layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/documentation/events"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/documentation/events'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-calendar layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Events</span>
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/documentation/songs"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/documentation/songs'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-book layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Songs</span>
                  </router-link>
                </li>
                <li v-if="authStore.isAuthenticated && authStore.isAdmin">
                  <router-link
                    to="/documentation/users"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/documentation/users'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-users layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Users</span>
                  </router-link>
                </li>
                <li v-if="authStore.isAuthenticated && authStore.isAdmin">
                  <router-link
                    to="/documentation/statistics"
                    :class="[
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      $route.path === '/documentation/statistics'
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                    ]"
                    @click="handleLinkClick"
                  >
                    <i class="pi pi-fw pi-chart-bar layout-menuitem-icon menu-icon"></i>
                    <span class="layout-menuitem-text">Statistics</span>
                  </router-link>
                </li>
              </ul>
            </details>
          </li>
        </template>

        <template v-if="authStore.isAuthenticated && authStore.isHelper">
          <LiveNavSection :live-events="liveEvents" @link-click="handleLinkClick" />
        </template>
      </ul>
    </div>

    <!-- User Profile Section -->
    <div
      v-if="authStore.isAuthenticated"
      class="sticky inset-x-0 bottom-0 border-t border-gray-100 dark:border-gray-700"
    >
      <div class="bg-white dark:bg-gray-900 p-4">
        <div class="flex items-center gap-2 mb-2">
          <div
            class="flex size-10 items-center justify-center rounded-full bg-gray-200 text-gray-600"
          >
            <span class="text-sm font-medium">
              {{ getUserInitials }}
            </span>
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-xs">
              <strong class="block font-medium truncate">{{
                authStore.user?.username || "User"
              }}</strong>
              <span class="truncate block text-gray-500">{{ authStore.user?.email || "" }}</span>
            </p>
          </div>
        </div>

        <a
          class="text-xs dark:text-gray-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150 cursor-pointer w-full text-gray-500"
          @click.prevent="handleLogout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M5.636 5.636a9 9 0 1012.728 0M12 3v9"
            />
          </svg>
          <span>Log out</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useAuthStore } from "../../stores/auth";
import { useRouter, useRoute } from "vue-router";
import LiveNavSection from "./LiveNavSection.vue";
import { useLiveEvents } from "../../composables/useLiveEvents";
import { EVENT_TYPE_OPTIONS } from "../../constants/eventTypes";

export default {
  name: "Sidebar",
  components: {
    LiveNavSection
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close"],
  setup(props, { emit }) {
    const authStore = useAuthStore();
    const router = useRouter();
    const route = useRoute();
    const { liveEvents } = useLiveEvents();

    const isEventsSectionActive = computed(() => route.path === "/events");

    const isAllEventsActive = computed(() => route.path === "/events" && !route.query.type);

    const isEventTypeFilterActive = (type) => route.path === "/events" && route.query.type === type;

    const getEventTypeIcon = (type) => {
      switch (type) {
        case "prevoting":
          return "pi-receipt";
        case "karaoke":
          return "pi-microphone";
        default:
          return "pi-ticket";
      }
    };

    // Close sidebar when clicking on a link (mobile only)
    const handleLinkClick = () => {
      if (props.isMobile) {
        emit("close");
      }
    };

    const getUserInitials = computed(() => {
      if (authStore.user?.username) {
        return authStore.user.username
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);
      }
      return "U";
    });

    const handleLogout = () => {
      authStore.logout();
      router.push("/login");
      if (props.isMobile) {
        emit("close");
      }
    };

    return {
      authStore,
      liveEvents,
      EVENT_TYPE_OPTIONS,
      isEventsSectionActive,
      isAllEventsActive,
      isEventTypeFilterActive,
      getEventTypeIcon,
      getUserInitials,
      handleLogout,
      handleLinkClick
    };
  }
};
</script>

<style scoped>
/* Menu icon colors - default state */
.menu-icon {
  color: #66b2b2;
  transition: color 0.15s ease-in-out;
}

/* Hover state for icons - only target the icon in the element being hovered */
a:hover .menu-icon,
summary:hover .menu-icon,
li > a:hover .menu-icon {
  color: #006666;
}

/* Active state - target router-link with active classes or bg-gray-100 */
:deep(.router-link-active .menu-icon),
:deep(.router-link-exact-active .menu-icon),
:deep(a.bg-gray-100 .menu-icon),
a.bg-gray-100 .menu-icon {
  color: #006666;
}

/* For summary items when details is open */
details[open] summary .menu-icon {
  color: #006666;
}
</style>
